// Hanafi Faraid algorithm with the standard Sunni rules.
// Implements: Furud, Asabah-bil-ghayr (sons/daughters, sonsSons/sonsDaughters, brothers/sisters),
// Asabah-ma'a-l-ghayr (full sisters with daughters), Hajb (blocking),
// Awl (proportional reduction when sum>1), Radd (proportional return when sum<1, no Asabah),
// Umariyatayn (spouse + father + mother → mother gets 1/3 of remainder).
//
// The algorithm is intentionally conservative:
// - Hanafi default (BD majority); madhhab toggle currently surfaces a textual note for
//   well-known differences but uses the same arithmetic for the standard cases.
// - Distant relatives (Dhul-Arham), grandfather-with-brothers competition (Maliki/Shafi'i/Hanbali),
//   and Akdariyya are intentionally out of scope of v1 — flagged in disclaimers.

import { F } from "./fraction";
import {
  type Heirs,
  type HeirGroup,
  type Madhhab,
  HEIR_LABELS,
  QURANIC_REFS,
} from "./types";

export interface ShareLine {
  group: HeirGroup;
  count: number;
  fraction: F;          // total group share (out of estate=1)
  perPerson: F;
  amount: number;       // BDT for whole group
  amountPer: number;    // BDT per individual heir
  rule: { en: string; bn: string };
  type: "furud" | "asabah" | "radd";
}

export interface BlockedLine {
  group: HeirGroup;
  count: number;
  reason: { en: string; bn: string };
}

export interface Step {
  en: string;
  bn: string;
  ref?: string;
}

export interface FaraidResult {
  estate: number;
  shares: ShareLine[];
  blocked: BlockedLine[];
  totalFurudBefore: F;     // sum of Furud before adjustment
  awlApplied: boolean;
  awlDenominator?: number; // if awl applied: new denominator
  raddApplied: boolean;
  asabahApplied: boolean;
  steps: Step[];
  notes: { en: string; bn: string }[];
  madhhab: Madhhab;
  unsupported?: { en: string; bn: string }[]; // edge cases we did not model
}

function reason(group: HeirGroup, en: string, bn: string): BlockedLine {
  return { group, count: 0, reason: { en, bn } };
}

export function calculateFaraid(estate: number, raw: Heirs, madhhab: Madhhab = "hanafi"): FaraidResult {
  // Defensive copy
  const h: Heirs = {
    ...raw,
    wives: Math.max(0, Math.min(4, Math.floor(raw.wives || 0))),
    sons: Math.max(0, Math.floor(raw.sons || 0)),
    daughters: Math.max(0, Math.floor(raw.daughters || 0)),
    sonsSons: Math.max(0, Math.floor(raw.sonsSons || 0)),
    sonsDaughters: Math.max(0, Math.floor(raw.sonsDaughters || 0)),
    fullBrothers: Math.max(0, Math.floor(raw.fullBrothers || 0)),
    fullSisters: Math.max(0, Math.floor(raw.fullSisters || 0)),
    paternalHalfBrothers: Math.max(0, Math.floor(raw.paternalHalfBrothers || 0)),
    paternalHalfSisters: Math.max(0, Math.floor(raw.paternalHalfSisters || 0)),
    maternalHalfSiblings: Math.max(0, Math.floor(raw.maternalHalfSiblings || 0)),
  };

  // Mutual exclusion: a deceased man has wives, a deceased woman has a husband.
  // Both flags simultaneously is invalid; we trust the UI to enforce it.
  const blocked: BlockedLine[] = [];
  const steps: Step[] = [];
  const notes: FaraidResult["notes"] = [];
  const unsupported: FaraidResult["unsupported"] = [];

  // ---------- HAJB (blocking) ----------
  // Father blocks paternal grandfather entirely.
  let pgf = h.paternalGrandfather && !h.father;
  if (h.paternalGrandfather && h.father) {
    blocked.push(reason("paternalGrandfather",
      "Excluded by the father.", "পিতার উপস্থিতিতে দাদা উত্তরাধিকার পান না।"));
  }
  // Mother blocks both grandmothers; father blocks paternal grandmother.
  let pgm = h.paternalGrandmother && !h.mother && !h.father;
  if (h.paternalGrandmother && (h.mother || h.father)) {
    blocked.push(reason("paternalGrandmother",
      h.mother ? "Excluded by the mother." : "Excluded by the father.",
      h.mother ? "মাতার উপস্থিতিতে দাদি উত্তরাধিকার পান না।" : "পিতার উপস্থিতিতে দাদি উত্তরাধিকার পান না।"));
  }
  let mgm = h.maternalGrandmother && !h.mother;
  if (h.maternalGrandmother && h.mother) {
    blocked.push(reason("maternalGrandmother",
      "Excluded by the mother.", "মাতার উপস্থিতিতে নানি উত্তরাধিকার পান না।"));
  }

  // Son blocks: son's son, son's daughter (entirely)
  if (h.sons > 0 && (h.sonsSons > 0 || h.sonsDaughters > 0)) {
    if (h.sonsSons > 0) blocked.push(reason("sonsSons",
      "Excluded by son(s) of the deceased.", "মৃতের পুত্রের উপস্থিতিতে পৌত্র উত্তরাধিকার পান না।"));
    if (h.sonsDaughters > 0) blocked.push(reason("sonsDaughters",
      "Excluded by son(s) of the deceased.", "মৃতের পুত্রের উপস্থিতিতে পৌত্রী উত্তরাধিকার পান না।"));
  }
  let sonsSons = h.sons > 0 ? 0 : h.sonsSons;
  let sonsDaughters = h.sons > 0 ? 0 : h.sonsDaughters;

  // Descendants summary (for spouse/mother/sibling decisions)
  const hasMaleDescendant = h.sons > 0 || sonsSons > 0;
  const hasAnyDescendant = h.sons > 0 || h.daughters > 0 || sonsSons > 0 || sonsDaughters > 0;

  // Siblings blocked by: any descendant, father, paternal grandfather (Hanafi).
  const siblingBlocker = hasAnyDescendant || h.father || pgf;
  if (siblingBlocker) {
    const reasonStr = (en: string, bn: string) => ({ en, bn });
    const ben = h.father ? "Excluded by the father." : (pgf ? "Excluded by the paternal grandfather (Hanafi)." : "Excluded by descendants.");
    const bbn = h.father ? "পিতার উপস্থিতিতে ভাই/বোন উত্তরাধিকার পান না।" : (pgf ? "দাদার উপস্থিতিতে ভাই/বোন উত্তরাধিকার পান না (হানাফী)।" : "সন্তান/পৌত্র-পৌত্রী থাকলে ভাই/বোন উত্তরাধিকার পান না।");
    if (h.fullBrothers > 0) blocked.push({ group: "fullBrothers", count: h.fullBrothers, reason: reasonStr(ben, bbn) });
    if (h.fullSisters > 0) blocked.push({ group: "fullSisters", count: h.fullSisters, reason: reasonStr(ben, bbn) });
    if (h.paternalHalfBrothers > 0) blocked.push({ group: "paternalHalfBrothers", count: h.paternalHalfBrothers, reason: reasonStr(ben, bbn) });
    if (h.paternalHalfSisters > 0) blocked.push({ group: "paternalHalfSisters", count: h.paternalHalfSisters, reason: reasonStr(ben, bbn) });
    if (h.maternalHalfSiblings > 0) blocked.push({ group: "maternalHalfSiblings", count: h.maternalHalfSiblings, reason: reasonStr(ben, bbn) });
  }
  const sibsActive = !siblingBlocker;
  const fullBrothers = sibsActive ? h.fullBrothers : 0;
  const fullSisters = sibsActive ? h.fullSisters : 0;
  let pHalfBrothers = sibsActive ? h.paternalHalfBrothers : 0;
  let pHalfSisters = sibsActive ? h.paternalHalfSisters : 0;
  const matHalves = sibsActive ? h.maternalHalfSiblings : 0;

  // Full brother blocks paternal half-brothers and (if no asabah-ma'a-l-ghayr) paternal half-sisters
  if (fullBrothers > 0 && (pHalfBrothers > 0 || pHalfSisters > 0)) {
    if (pHalfBrothers > 0) blocked.push({ group: "paternalHalfBrothers", count: pHalfBrothers, reason: { en: "Excluded by full brother(s).", bn: "সহোদর ভাইয়ের উপস্থিতিতে বৈমাত্রেয় ভাই উত্তরাধিকার পান না।" } });
    if (pHalfSisters > 0) blocked.push({ group: "paternalHalfSisters", count: pHalfSisters, reason: { en: "Excluded by full brother(s).", bn: "সহোদর ভাইয়ের উপস্থিতিতে বৈমাত্রেয় বোন উত্তরাধিকার পান না।" } });
    pHalfBrothers = 0; pHalfSisters = 0;
  }

  // ---------- FURUD (fixed shares) ----------
  // Counts of "siblings or more" for mother's reduction (≥2 of any siblings, even if blocked)
  const siblingCountForMother =
    h.fullBrothers + h.fullSisters +
    h.paternalHalfBrothers + h.paternalHalfSisters +
    h.maternalHalfSiblings;
  const motherReducesTo16 = hasAnyDescendant || siblingCountForMother >= 2;

  const lines: ShareLine[] = [];
  const pushLine = (
    group: HeirGroup, count: number, fraction: F, type: ShareLine["type"],
    rule: { en: string; bn: string },
  ) => {
    if (count <= 0 || fraction.isZero()) return;
    lines.push({
      group, count, fraction,
      perPerson: new F(fraction.n, fraction.d * count),
      amount: 0, amountPer: 0,  // filled later
      rule, type,
    });
  };

  // Spouse
  if (h.husband) {
    const f = hasAnyDescendant ? new F(1, 4) : new F(1, 2);
    pushLine("husband", 1, f, "furud", {
      en: hasAnyDescendant ? "1/4 because the deceased has descendants." : "1/2 because no descendants.",
      bn: hasAnyDescendant ? "মৃতের সন্তান/পৌত্র-পৌত্রী আছে — স্বামী ১/৪।" : "মৃতের সন্তান/পৌত্র-পৌত্রী নেই — স্বামী ১/২।",
    });
    steps.push({
      en: `Husband: ${f.toString()} (${hasAnyDescendant ? "with descendants" : "no descendants"}). Surah An-Nisa 4:12.`,
      bn: `স্বামী: ${f.toString()} (${hasAnyDescendant ? "সন্তান আছে" : "সন্তান নেই"})। ${QURANIC_REFS.childrenSpouse.bn}।`,
      ref: QURANIC_REFS.childrenSpouse.ref,
    });
  }
  if (h.wives > 0) {
    const f = hasAnyDescendant ? new F(1, 8) : new F(1, 4);
    pushLine("wives", h.wives, f, "furud", {
      en: hasAnyDescendant ? "1/8 (shared) because deceased has descendants." : "1/4 (shared) because no descendants.",
      bn: hasAnyDescendant ? "সন্তান আছে — স্ত্রী ১/৮ (একাধিক হলে সমান ভাগ)।" : "সন্তান নেই — স্ত্রী ১/৪ (একাধিক হলে সমান ভাগ)।",
    });
    steps.push({
      en: `Wife/Wives (${h.wives}): ${f.toString()} shared equally. Surah An-Nisa 4:12.`,
      bn: `স্ত্রী (${h.wives}): ${f.toString()} সমান ভাগে। ${QURANIC_REFS.childrenSpouse.bn}।`,
      ref: QURANIC_REFS.childrenSpouse.ref,
    });
  }

  // Father — Furud share if there is descendant
  let fatherFurud: F | null = null;
  if (h.father) {
    if (hasAnyDescendant) {
      fatherFurud = new F(1, 6);
      pushLine("father", 1, fatherFurud, "furud", {
        en: "1/6 because deceased has descendants.",
        bn: "সন্তান/পৌত্র-পৌত্রী আছে — পিতা ১/৬।",
      });
      steps.push({
        en: `Father: 1/6 (descendants present). Surah An-Nisa 4:11.`,
        bn: `পিতা: ১/৬ (সন্তান আছে)। ${QURANIC_REFS.childrenSpouse.bn}।`,
        ref: QURANIC_REFS.childrenSpouse.ref,
      });
    }
  }
  // Paternal grandfather (when no father) — Furud 1/6 if descendant
  let pgfFurud: F | null = null;
  if (pgf) {
    if (hasAnyDescendant) {
      pgfFurud = new F(1, 6);
      pushLine("paternalGrandfather", 1, pgfFurud, "furud", {
        en: "1/6 (acts as substitute for the father, with descendants present).",
        bn: "পিতার অনুপস্থিতিতে দাদা পিতার স্থলাভিষিক্ত — সন্তান থাকায় ১/৬।",
      });
    }
  }

  // Mother — handle Umariyatayn (Hanafi standard view)
  // Spouse + Mother + Father, no descendants, no ≥2 siblings
  // Mother gets 1/3 of REMAINDER after spouse.
  let motherShare: F | null = null;
  let umariyatayn = false;
  if (h.mother) {
    const noSibsForUm = siblingCountForMother === 0;
    const noKids = !hasAnyDescendant;
    if (noKids && noSibsForUm && h.father && (h.husband || h.wives > 0)) {
      umariyatayn = true;
      const spouseShare = h.husband ? new F(1, 2) : new F(1, 4);
      const remainder = F.one.sub(spouseShare);
      motherShare = remainder.div(new F(3, 1)); // 1/3 of remainder
      pushLine("mother", 1, motherShare, "furud", {
        en: "Umariyatayn rule: 1/3 of the remainder after the spouse — not 1/3 of the whole estate.",
        bn: "উমারিয়াতাইন বিধান: স্ত্রী/স্বামীর অংশ বাদে অবশিষ্টের ১/৩ — পুরো সম্পত্তির ১/৩ নয়।",
      });
      steps.push({
        en: `Umariyatayn case: Mother = (1 − ${spouseShare}) × 1/3 = ${motherShare}.`,
        bn: `উমারিয়াতাইন কেস: মাতা = (১ − ${spouseShare}) × ১/৩ = ${motherShare}।`,
      });
    } else {
      const f = motherReducesTo16 ? new F(1, 6) : new F(1, 3);
      motherShare = f;
      pushLine("mother", 1, f, "furud", {
        en: motherReducesTo16
          ? "1/6 because there are descendants or two-or-more siblings."
          : "1/3 (no descendants, fewer than two siblings).",
        bn: motherReducesTo16
          ? "সন্তান বা ২+ ভাই/বোন আছে — মাতা ১/৬।"
          : "সন্তান নেই, ভাই/বোন ২ এর কম — মাতা ১/৩।",
      });
      steps.push({
        en: `Mother: ${f.toString()} (${motherReducesTo16 ? "with descendants/≥2 siblings" : "no descendants, <2 siblings"}). Surah An-Nisa 4:11.`,
        bn: `মাতা: ${f.toString()}। ${QURANIC_REFS.childrenSpouse.bn}।`,
      });
    }
  }
  // Grandmothers (if mother absent)
  if (pgm || mgm) {
    const count = (pgm ? 1 : 0) + (mgm ? 1 : 0);
    const total = new F(1, 6);
    if (pgm) pushLine("paternalGrandmother", 1, count === 2 ? new F(1, 12) : total, "furud", {
      en: "Grandmother(s) share 1/6 equally.",
      bn: "দাদি/নানি ১/৬ — উভয়ে থাকলে সমান ভাগ।",
    });
    if (mgm) pushLine("maternalGrandmother", 1, count === 2 ? new F(1, 12) : total, "furud", {
      en: "Grandmother(s) share 1/6 equally.",
      bn: "দাদি/নানি ১/৬ — উভয়ে থাকলে সমান ভাগ।",
    });
    steps.push({
      en: `Grandmother(s): 1/6 total ${count === 2 ? "(split equally)" : ""}.`,
      bn: `দাদি/নানি: মোট ১/৬ ${count === 2 ? "(সমান ভাগ)" : ""}।`,
    });
  }

  // Daughters — Furud only if no son
  if (h.daughters > 0 && h.sons === 0) {
    const f = h.daughters === 1 ? new F(1, 2) : new F(2, 3);
    pushLine("daughters", h.daughters, f, "furud", {
      en: h.daughters === 1 ? "Single daughter: 1/2." : "Two or more daughters: 2/3 shared equally.",
      bn: h.daughters === 1 ? "একমাত্র কন্যা: ১/২।" : "দুই বা ততোধিক কন্যা: ২/৩ সমান ভাগে।",
    });
    steps.push({
      en: `Daughters (${h.daughters}): ${f.toString()}. Surah An-Nisa 4:11.`,
      bn: `কন্যা (${h.daughters}): ${f.toString()}।`,
    });
  }
  // Son's daughters — Furud only if no son and no son's son
  if (sonsDaughters > 0 && h.sons === 0 && sonsSons === 0) {
    let f: F | null = null;
    let rule: { en: string; bn: string } | null = null;
    if (h.daughters === 0) {
      f = sonsDaughters === 1 ? new F(1, 2) : new F(2, 3);
      rule = {
        en: sonsDaughters === 1 ? "Single son's daughter: 1/2." : "Two or more son's daughters: 2/3 shared.",
        bn: sonsDaughters === 1 ? "একমাত্র পৌত্রী: ১/২।" : "দুই বা ততোধিক পৌত্রী: ২/৩ সমান ভাগে।",
      };
    } else if (h.daughters === 1) {
      f = new F(1, 6); // completes 2/3 with the daughter's 1/2
      rule = {
        en: "Son's daughter(s): 1/6 to complete 2/3 alongside the daughter.",
        bn: "পৌত্রী: ১/৬ (কন্যার ১/২ এর সাথে মিলে ২/৩ পূর্ণ করে)।",
      };
    } // else: blocked by 2+ daughters with no son's son — falls through.
    if (f) {
      pushLine("sonsDaughters", sonsDaughters, f, "furud", rule!);
      steps.push({ en: `Son's daughters (${sonsDaughters}): ${f.toString()}.`, bn: `পৌত্রী (${sonsDaughters}): ${f.toString()}।` });
    } else {
      blocked.push({ group: "sonsDaughters", count: sonsDaughters, reason: {
        en: "Two or more daughters fully consume the female 2/3 — son's daughters get nothing in the absence of a son's son.",
        bn: "দুই বা ততোধিক কন্যা পুরো ২/৩ নিয়ে নেয় — পৌত্র না থাকলে পৌত্রী কিছু পান না।",
      } });
      sonsDaughters = 0; // effectively blocked
    }
  }

  // Sisters Furud (only when no descendants/father/PGF)
  // Asabah-ma'a-l-ghayr: full sisters become Asabah with daughters / sonsDaughters (no father).
  let fullSistersAsabahMaal = false;
  let pHalfSistersAsabahMaal = false;
  if (sibsActive && (h.daughters > 0 || sonsDaughters > 0)) {
    if (fullSisters > 0) {
      fullSistersAsabahMaal = true;
      // Will be paid as residue later — don't push Furud now.
    } else if (pHalfSisters > 0) {
      pHalfSistersAsabahMaal = true;
    }
  }
  if (sibsActive && fullBrothers === 0 && fullSisters > 0 && !fullSistersAsabahMaal) {
    const f = fullSisters === 1 ? new F(1, 2) : new F(2, 3);
    pushLine("fullSisters", fullSisters, f, "furud", {
      en: fullSisters === 1 ? "Single full sister: 1/2." : "Two or more full sisters: 2/3 shared.",
      bn: fullSisters === 1 ? "একমাত্র সহোদর বোন: ১/২।" : "দুই বা ততোধিক সহোদর বোন: ২/৩।",
    });
    steps.push({ en: `Full sisters (${fullSisters}): ${f.toString()}. Surah An-Nisa 4:176.`, bn: `সহোদর বোন (${fullSisters}): ${f.toString()}। ${QURANIC_REFS.kalala.bn}।` });
  }
  if (sibsActive && pHalfBrothers === 0 && pHalfSisters > 0 && !pHalfSistersAsabahMaal && fullSisters < 2 && !fullSistersAsabahMaal) {
    let f: F | null = null;
    let rule: { en: string; bn: string } | null = null;
    if (fullSisters === 0) {
      f = pHalfSisters === 1 ? new F(1, 2) : new F(2, 3);
      rule = {
        en: pHalfSisters === 1 ? "Single paternal half-sister: 1/2." : "Two or more paternal half-sisters: 2/3 shared.",
        bn: pHalfSisters === 1 ? "একমাত্র বৈমাত্রেয় বোন: ১/২।" : "দুই বা ততোধিক বৈমাত্রেয় বোন: ২/৩।",
      };
    } else if (fullSisters === 1) {
      f = new F(1, 6); // complete 2/3
      rule = {
        en: "Paternal half-sister(s): 1/6 to complete 2/3 alongside one full sister.",
        bn: "বৈমাত্রেয় বোন: ১/৬ — একজন সহোদর বোনের সাথে মিলে ২/৩ পূর্ণ করে।",
      };
    }
    if (f) {
      pushLine("paternalHalfSisters", pHalfSisters, f, "furud", rule!);
      steps.push({ en: `Paternal half-sisters (${pHalfSisters}): ${f.toString()}.`, bn: `বৈমাত্রেয় বোন (${pHalfSisters}): ${f.toString()}।` });
    }
  } else if (sibsActive && pHalfSisters > 0 && fullSisters >= 2 && !pHalfSistersAsabahMaal) {
    blocked.push({ group: "paternalHalfSisters", count: pHalfSisters, reason: {
      en: "Excluded by two or more full sisters consuming 2/3.",
      bn: "দুই+ সহোদর বোন ২/৩ নিয়ে নেয় — বৈমাত্রেয় বোন কিছু পান না।",
    }});
    pHalfSisters = 0;
  }
  // Maternal half-siblings
  if (sibsActive && matHalves > 0) {
    const f = matHalves === 1 ? new F(1, 6) : new F(1, 3);
    pushLine("maternalHalfSiblings", matHalves, f, "furud", {
      en: matHalves === 1 ? "Single maternal half-sibling: 1/6." : "Two or more maternal half-siblings: 1/3, shared equally regardless of gender.",
      bn: matHalves === 1 ? "একমাত্র বৈপিত্রেয় ভাই/বোন: ১/৬।" : "দুই বা ততোধিক বৈপিত্রেয় ভাই/বোন: ১/৩ — পুরুষ ও নারী সমান ভাগে।",
    });
    steps.push({ en: `Maternal half-siblings (${matHalves}): ${f.toString()}. Surah An-Nisa 4:12.`, bn: `বৈপিত্রেয় ভাই/বোন (${matHalves}): ${f.toString()}। ${QURANIC_REFS.maternalHalves.bn}।` });
  }

  // ---------- AWL (proportional reduction) ----------
  let totalFurud = lines.filter(l => l.type === "furud").reduce((a, l) => a.add(l.fraction), F.zero);
  let awlApplied = false;
  let awlDenominator: number | undefined;
  if (totalFurud.cmp(F.one) > 0) {
    awlApplied = true;
    // bring all to common denominator d, then new denominator = sum of numerators
    // Each share num_i / sum_num.
    // Using existing fractions: fraction.div(totalFurud) → num/sum_num if same d.
    // Simpler: scale every share by 1/totalFurud.
    const factor = F.one.div(totalFurud);
    for (const l of lines) {
      if (l.type === "furud") {
        const newF = l.fraction.mul(factor);
        l.fraction = newF;
        l.perPerson = new F(newF.n, newF.d * l.count);
      }
    }
    // For display: original common denominator = lcm of all denoms; new denom = sum of numerators
    const denoms = lines.filter(l => l.type === "furud").map(l => l.fraction.d);
    const d = denoms.reduce((a, b) => (a * b) / gcdLocal(a, b), 1);
    awlDenominator = d;
    steps.push({
      en: `Awl: shares sum to ${totalFurud.toString()} > 1. Reduce proportionally so all heirs receive less but in correct ratio.`,
      bn: `আউল: ফরাইজের যোগফল ${totalFurud.toString()} > ১। আনুপাতিকভাবে কমিয়ে সবাইকে সঠিক অনুপাতে কম দেওয়া হয়।`,
    });
    totalFurud = F.one;
  }

  // ---------- ASABAH (residuary heirs take what's left) ----------
  let residue = F.one.sub(totalFurud);
  let asabahApplied = false;

  // 1) Sons / daughters: ratio 2:1 ("li-dhakari mithlu hazzi al-unthayayn")
  if (residue.isPositive() && (h.sons > 0)) {
    asabahApplied = true;
    const parts = h.sons * 2 + h.daughters;
    if (h.sons > 0) {
      const f = residue.mul(new F(h.sons * 2, parts));
      pushLine("sons", h.sons, f, "asabah", {
        en: "Residue distributed to sons and daughters in 2:1 (Surah An-Nisa 4:11).",
        bn: "অবশিষ্ট পুত্র-কন্যাদের মধ্যে ২ঃ১ অনুপাতে (সূরা আন-নিসা ৪:১১)।",
      });
    }
    if (h.daughters > 0) {
      const f = residue.mul(new F(h.daughters, parts));
      pushLine("daughters", h.daughters, f, "asabah", {
        en: "Daughters become Asabah with sons in 2:1 ratio.",
        bn: "পুত্রের সাথে কন্যা আসাবা — ২ঃ১ অনুপাতে।",
      });
    }
    steps.push({ en: `Sons + daughters take residue ${residue.toString()} in ${h.sons * 2}:${h.daughters} ratio.`, bn: `পুত্র + কন্যা অবশিষ্ট ${residue.toString()} নেয়, অনুপাত ${h.sons * 2}ঃ${h.daughters}।` });
    residue = F.zero;
  }

  // 2) If only sonsSons or sonsSons + sonsDaughters (no son): they become Asabah
  if (residue.isPositive() && sonsSons > 0) {
    asabahApplied = true;
    const parts = sonsSons * 2 + sonsDaughters;
    pushLine("sonsSons", sonsSons, residue.mul(new F(sonsSons * 2, parts)), "asabah", {
      en: "Son's sons take residue (with son's daughters in 2:1).",
      bn: "পৌত্র অবশিষ্ট নেয় (পৌত্রীর সাথে ২ঃ১)।",
    });
    if (sonsDaughters > 0) {
      pushLine("sonsDaughters", sonsDaughters, residue.mul(new F(sonsDaughters, parts)), "asabah", {
        en: "Son's daughters become Asabah alongside son's sons in 2:1.",
        bn: "পৌত্রের সাথে পৌত্রী আসাবা — ২ঃ১ অনুপাতে।",
      });
    }
    steps.push({ en: `Son's sons + son's daughters take residue ${residue.toString()}.`, bn: `পৌত্র + পৌত্রী অবশিষ্ট নেয়।` });
    residue = F.zero;
  }

  // 3) Father as residuary (only when no descendants — father had no Furud above, OR with daughters only)
  if (h.father && residue.isPositive() && !hasMaleDescendant) {
    asabahApplied = true;
    pushLine("father", 1, residue, "asabah", {
      en: hasAnyDescendant
        ? "Father takes the residue (Asabah) in addition to his 1/6 — there are only daughters, not male descendants."
        : "Father takes the entire residue as the closest male relative (Asabah).",
      bn: hasAnyDescendant
        ? "পিতা ১/৬ এর পাশাপাশি অবশিষ্টও পান — শুধু কন্যা/পৌত্রী আছে, পুত্র/পৌত্র নেই।"
        : "পিতা সবচেয়ে নিকটবর্তী পুরুষ আত্মীয় হিসেবে অবশিষ্ট পান (আসাবা)।",
    });
    steps.push({ en: `Father takes residue ${residue.toString()} as Asabah.`, bn: `পিতা অবশিষ্ট ${residue.toString()} আসাবা হিসেবে নেন।` });
    residue = F.zero;
  }

  // 4) Paternal grandfather as residuary (when no father, no male descendant)
  if (pgf && residue.isPositive() && !hasMaleDescendant && !h.father) {
    asabahApplied = true;
    pushLine("paternalGrandfather", 1, residue, "asabah", {
      en: "Paternal grandfather takes the residue as a substitute for the father (Hanafi).",
      bn: "পিতার অনুপস্থিতিতে দাদা পিতার স্থলাভিষিক্ত — অবশিষ্ট নেন (হানাফী)।",
    });
    steps.push({ en: `PGF takes residue ${residue.toString()}.`, bn: `দাদা অবশিষ্ট ${residue.toString()} নেন।` });
    residue = F.zero;
  }

  // 5) Full sisters as Asabah-ma'a-l-ghayr (with daughters/sonsDaughters)
  if (fullSistersAsabahMaal && residue.isPositive()) {
    asabahApplied = true;
    if (fullBrothers > 0) {
      // Mixed: shouldn't really happen since brother+sisters = Asabah-bil-ghayr
    }
    // No full brother (otherwise would have been bilghayr below). All sisters take residue.
    pushLine("fullSisters", fullSisters, residue, "asabah", {
      en: "Full sisters become Asabah-ma'a-l-ghayr with the daughter(s) — they share the residue equally.",
      bn: "কন্যার সাথে সহোদর বোন আসাবা-মা'আল-গায়র — অবশিষ্ট সমান ভাগে নেন।",
    });
    steps.push({ en: `Full sisters take residue ${residue.toString()} as Asabah with daughters.`, bn: `সহোদর বোন কন্যার সাথে আসাবা হয়ে অবশিষ্ট নেন।` });
    residue = F.zero;
  } else if (pHalfSistersAsabahMaal && residue.isPositive() && fullSisters === 0) {
    asabahApplied = true;
    pushLine("paternalHalfSisters", pHalfSisters, residue, "asabah", {
      en: "Paternal half-sisters become Asabah-ma'a-l-ghayr with the daughter(s).",
      bn: "কন্যার সাথে বৈমাত্রেয় বোন আসাবা-মা'আল-গায়র।",
    });
    residue = F.zero;
  }

  // 6) Full brothers (with sisters 2:1) — Asabah-bil-ghayr
  if (residue.isPositive() && fullBrothers > 0) {
    asabahApplied = true;
    const parts = fullBrothers * 2 + fullSisters;
    pushLine("fullBrothers", fullBrothers, residue.mul(new F(fullBrothers * 2, parts)), "asabah", {
      en: "Full brothers take residue with full sisters in 2:1.",
      bn: "সহোদর ভাই অবশিষ্ট নেন, বোনের সাথে ২ঃ১।",
    });
    if (fullSisters > 0) {
      pushLine("fullSisters", fullSisters, residue.mul(new F(fullSisters, parts)), "asabah", {
        en: "Full sisters become Asabah with full brothers in 2:1.",
        bn: "ভাইয়ের সাথে সহোদর বোন আসাবা — ২ঃ১।",
      });
    }
    steps.push({ en: `Full siblings take residue ${residue.toString()}.`, bn: `সহোদর ভাই-বোন অবশিষ্ট নেয়।` });
    residue = F.zero;
  }

  // 7) Paternal half-brothers (with sisters 2:1)
  if (residue.isPositive() && pHalfBrothers > 0) {
    asabahApplied = true;
    const parts = pHalfBrothers * 2 + pHalfSisters;
    pushLine("paternalHalfBrothers", pHalfBrothers, residue.mul(new F(pHalfBrothers * 2, parts)), "asabah", {
      en: "Paternal half-brothers take residue with paternal half-sisters in 2:1.",
      bn: "বৈমাত্রেয় ভাই অবশিষ্ট নেন, বোনের সাথে ২ঃ১।",
    });
    if (pHalfSisters > 0) {
      pushLine("paternalHalfSisters", pHalfSisters, residue.mul(new F(pHalfSisters, parts)), "asabah", {
        en: "Paternal half-sisters become Asabah with their brothers in 2:1.",
        bn: "ভাইয়ের সাথে বৈমাত্রেয় বোন আসাবা — ২ঃ১।",
      });
    }
    residue = F.zero;
  }

  // ---------- RADD (proportional return) ----------
  let raddApplied = false;
  if (residue.isPositive() && !asabahApplied) {
    // Distribute residue among Furud heirs except spouse, proportionally to their share.
    const eligible = lines.filter(l => l.type === "furud" && l.group !== "husband" && l.group !== "wives");
    const sumElig = eligible.reduce((a, l) => a.add(l.fraction), F.zero);
    if (sumElig.isPositive()) {
      raddApplied = true;
      // factor = 1 / sumElig (so all eligible share the remaining 1 - spouse)
      const remainingForElig = F.one.sub(lines.filter(l => l.group === "husband" || l.group === "wives").reduce((a, l) => a.add(l.fraction), F.zero));
      // Each eligible new share = (l.fraction / sumElig) * remainingForElig
      for (const l of eligible) {
        const newF = l.fraction.div(sumElig).mul(remainingForElig);
        l.fraction = newF;
        l.perPerson = new F(newF.n, newF.d * l.count);
        l.type = "radd";
      }
      steps.push({
        en: `Radd: no Asabah; remaining ${residue.toString()} returned proportionally to fixed-share heirs (excluding spouse).`,
        bn: `রাদ্দ: কোনো আসাবা নেই; অবশিষ্ট ${residue.toString()} স্বামী/স্ত্রী বাদে ফরাইজ-অংশীদারদের আনুপাতিকভাবে ফিরিয়ে দেওয়া হয়।`,
      });
      residue = F.zero;
    }
  }

  // ---------- Compute amounts ----------
  for (const l of lines) {
    l.amount = l.fraction.toNumber() * estate;
    l.amountPer = l.amount / l.count;
  }

  // Notes by madhhab
  if (madhhab !== "hanafi") {
    notes.push({
      en: `${madhhabLabel(madhhab).en}: shares for typical cases match Hanafi. Differences mainly appear in grandfather-with-brothers competition, Mushtarakah (joint case), and Akdariyya (Maliki) — not yet modelled.`,
      bn: `${madhhabLabel(madhhab).bn}: সাধারণ ক্ষেত্রে অংশ হানাফীর সমান। দাদা-ভাইয়ের প্রতিযোগিতা, মুশতারাকা ও আকদারিয়া জাতীয় বিশেষ ক্ষেত্রে পার্থক্য আছে — এ ভার্সনে অন্তর্ভুক্ত নয়।`,
    });
  }

  // Sanity check: if no heirs at all
  if (lines.length === 0 && residue.eq(F.one)) {
    notes.push({
      en: "No qualifying heirs entered. The estate would go to the public treasury (Bait al-Mal) under classical rules.",
      bn: "কোনো উত্তরাধিকারী যোগ করা হয়নি। শাস্ত্রীয় বিধানে এই সম্পত্তি বায়তুল মাল-এ যাবে।",
    });
  }

  // Note unsupported chains if user entered distant-only families
  if (lines.length === 0 && (h.husband || h.wives > 0) && residue.isPositive()) {
    notes.push({
      en: "Only spouse present — spouse takes their fixed share; remainder goes to Bait al-Mal (no Radd to spouse classically).",
      bn: "শুধু স্বামী/স্ত্রী আছে — তিনি তাঁর ফরাইজ অংশ পাবেন; অবশিষ্ট বায়তুল মাল-এ যাবে (শাস্ত্রীয় বিধানে স্বামী/স্ত্রী রাদ্দ পান না)।",
    });
  }

  return {
    estate,
    shares: lines,
    blocked,
    totalFurudBefore: totalFurud,
    awlApplied,
    awlDenominator,
    raddApplied,
    asabahApplied,
    steps,
    notes,
    madhhab,
    unsupported,
  };
}

function gcdLocal(a: number, b: number): number {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { [a, b] = [b, a % b]; }
  return a || 1;
}

function madhhabLabel(m: Madhhab): { en: string; bn: string } {
  const map: Record<Madhhab, { en: string; bn: string }> = {
    hanafi: { en: "Hanafi", bn: "হানাফী" },
    shafii: { en: "Shafi'i", bn: "শাফি'ঈ" },
    maliki: { en: "Maliki", bn: "মালিকী" },
    hanbali: { en: "Hanbali", bn: "হাম্বলী" },
  };
  return map[m];
}

export { madhhabLabel, HEIR_LABELS };
